// Libs
import classNames from 'classnames/bind';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
// Components, Layouts, Pages
import { BaseInput, Loading } from '~/components/common';
// Others
import { useAppDispatch } from '~/utils/redux/hooks';
import { createFolder, editFolder } from '~/thunks/folder/folderThunk';
import { folderActions } from '~/thunks/folder/folderSlice';
import { BusinessTypeEnum } from '~/utils/enum/common';
import { defaultValuesForm, schema } from './helper';
import { formatTitleCase } from '~/utils/helper/common';
import { IEditFolder, IFolder, IFormFolder } from '~/utils/interface/folder';
import { DEFAULT_NUMBER_ZERO } from '~/utils/constants/common';
// Styles, images, icons
import styles from './FormFolder.module.scss';

type Props = {
  folder?: IFolder;
  businessType: BusinessTypeEnum;
  onClose: () => void;
  onSave?: (data: IFormFolder) => void;
};

const cx = classNames.bind(styles);

const FormFolder = (props: Props) => {
  //#region Destructuring Props
  const { folder, businessType, onClose, onSave } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/base']);
  const dispatch = useAppDispatch();
  const { id: targetId } = useParams();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<IFormFolder>({
    resolver: yupResolver(schema(t)),
    defaultValues: defaultValuesForm,
  });
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!folder) return;

    const body: IFormFolder = {
      name: folder.name,
      isCurrentApply: folder.isCurrentApply,
    };

    reset(body);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folder]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleCloseFormFolder = () => {
    onClose && onClose();
    reset();
  };

  const handleSubmitFormFolder = (data: IFormFolder) => {
    if (onSave) {
      onSave({
        ...(folder?.id ? { id: folder?.id } : {}),
        ...data,
      });
      return;
    }

    if (folder) {
      handleUpdateFolder(data);
    } else {
      handleCreateFolder({ ...data, businessId: targetId, businessType: businessType });
    }
  };

  const handleUpdateFolder = (data: IFormFolder) => {
    if (!folder) return;

    setIsLoading(true);

    const body: IFormFolder = {
      name: data.name,
      isCurrentApply: data.isCurrentApply,
    };

    const payload: IEditFolder = {
      folderId: folder.id,
      body: body,
    };

    dispatch(editFolder(payload))
      .unwrap()
      .then((res) => {
        dispatch(folderActions.setRefreshFolderList(true));
        handleCloseFormFolder();
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleCreateFolder = (data: IFormFolder) => {
    setIsLoading(true);

    dispatch(createFolder(data))
      .unwrap()
      .then((res) => {
        dispatch(folderActions.setRefreshFolderList(true));
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
        handleCloseFormFolder();
      });
  };
  //#endregion Handle Function

  return (
    <Dialog id='formFolderComponent' open={true} onClose={handleCloseFormFolder} fullWidth maxWidth={'sm'}>
      <Box component={'form'}>
        <DialogTitle>
          {t(
            folder && Object.keys(folder).length > DEFAULT_NUMBER_ZERO
              ? 'form_folder_title_edit'
              : 'form_folder_title'
          )}
        </DialogTitle>

        <DialogContent dividers={true}>
          <Grid2 container rowSpacing={'24px'} columnSpacing={'24px'}>
            <Grid2 size={12}>
              <Controller
                name='name'
                control={control}
                render={({ field: { value, onChange } }) => (
                  <BaseInput
                    required
                    label={t('form_folder_label_name')}
                    value={value}
                    onChange={onChange}
                    errorMessage={errors?.name?.message}
                  />
                )}
              />
            </Grid2>

            <Grid2 size={12}>
              <Stack direction={'row'} alignItems={'center'} rowGap={'8px'} columnGap={'8px'}>
                <Typography component={'span'} className={cx('labelSwitch')}>
                  {t('form_folder_label_apply_to_current', {
                    name: formatTitleCase(businessType),
                  })}
                </Typography>

                <Controller
                  name='isCurrentApply'
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <Switch checked={value} onChange={(e) => onChange(e.target.checked)} size='small' />
                  )}
                />
              </Stack>
            </Grid2>
          </Grid2>

          {isLoading && <Loading />}
        </DialogContent>

        <DialogActions>
          <Button variant='outlined' type='button' onClick={handleCloseFormFolder}>
            {t('common_action_cancel', { ns: 'common/base' })}
          </Button>

          <Button variant='contained' type='button' onClick={handleSubmit(handleSubmitFormFolder)}>
            {t('common_action_save', { ns: 'common/base' })}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default FormFolder;
